/**
 * ĐỔI CA / NHẬN CA TRỐNG.
 *
 * Three swap types, all served by POST /staff/shift-swaps:
 *   SWAP        — my shift for a named colleague's shift
 *   GIVEAWAY    — give my shift away (optionally to a named colleague)
 *   PICKUP_OPEN — pick up an unassigned shift
 *
 * FULLY WIRED NOW:
 *   - ca trống       GET /staff/open-shifts (skill-gated and de-conflicted
 *                    server-side), with "Nhận ca này" selecting the slot;
 *   - đồng nghiệp    GET /staff/colleagues — name + role + shift ONLY, exactly
 *                    the privacy boundary the server enforces. Picking a
 *                    colleague fills targetEmployeeId (+ targetAssignmentId
 *                    for SWAP) — no more typing opaque ids;
 *   - yêu cầu        GET /staff/shift-swaps — both directions (SENT/RECEIVED)
 *                    with status and the manager's decision note.
 *
 * ONE SERVER LIMITATION, handled visibly: `shift_swap_requests` has no column
 * for WHICH open shift a PICKUP_OPEN request targets, so the chosen slot is
 * appended to the reason text (where the approving manager reads it). The
 * appended sentence is shown to the employee before sending — nothing is
 * altered silently.
 */
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  errorMessage,
  queryKeys,
  useColleagues,
  useOpenShifts,
  useShiftSwaps,
  useShifts,
} from '../lib/queries';
import { SWAP_TYPE_LABEL, approvalStatusLabel, approvalStatusTone } from '../lib/labels';
import type { Colleague, OpenShift, ShiftSwapItem, ShiftSwapResponse, SwapType } from '../lib/contracts';
import { formatTime, formatTimeDate, toDateInput, weekdayShort } from '../lib/format';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  SectionHeader,
  StatusBadge,
} from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';
import { useToast } from '../components/Toast';
import { useSyncState } from '../lib/hooks';

const schema = z.object({
  swapType: z.enum(['SWAP', 'GIVEAWAY', 'PICKUP_OPEN']),
  requesterAssignmentId: z.string().min(1, 'Chọn ca làm của bạn.'),
  targetEmployeeId: z.string().max(32),
  targetAssignmentId: z.string().max(32),
  reason: z.string().min(5, 'Lý do phải có ít nhất 5 ký tự.').max(1000),
});

type FormValues = z.infer<typeof schema>;

export function ShiftSwapScreen(): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { online } = useSyncState();
  const [created, setCreated] = useState<ShiftSwapResponse | null>(null);
  const [pickedOpenShift, setPickedOpenShift] = useState<OpenShift | null>(null);
  const [pickedColleague, setPickedColleague] = useState<Colleague | null>(null);

  // Only future shifts can meaningfully be swapped.
  const from = toDateInput(new Date());
  const to = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return toDateInput(d);
  }, []);
  const shifts = useShifts(from, to);
  const swapList = useShiftSwaps();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      swapType: 'SWAP',
      requesterAssignmentId: '',
      targetEmployeeId: '',
      targetAssignmentId: '',
      reason: '',
    },
  });

  const swapType = watch('swapType');
  const requesterAssignmentId = watch('requesterAssignmentId');

  // Colleagues are filtered around the shift being swapped when one is chosen
  // (the server widens to ±7 days of that shift's date).
  const requesterShiftId = shifts.data?.shifts.find((s) => s.assignmentId === requesterAssignmentId)?.shiftId;
  const colleagues = useColleagues(requesterShiftId, swapType !== 'PICKUP_OPEN');
  const openShifts = useOpenShifts(swapType === 'PICKUP_OPEN');

  const pickColleague = (colleague: Colleague): void => {
    setPickedColleague(colleague);
    setValue('targetEmployeeId', colleague.employeeId, { shouldValidate: true });
    if (swapType === 'SWAP') {
      setValue('targetAssignmentId', colleague.assignmentId, { shouldValidate: true });
    }
  };

  const clearColleague = (): void => {
    setPickedColleague(null);
    setValue('targetEmployeeId', '');
    setValue('targetAssignmentId', '');
  };

  /** The sentence appended for a PICKUP_OPEN — shown before sending. */
  const pickupNote =
    pickedOpenShift === null
      ? null
      : `Nhận ca trống ${pickedOpenShift.businessDate} ${formatTime(pickedOpenShift.startAt)}–${formatTime(
          pickedOpenShift.endAt,
        )} (mã ca ${pickedOpenShift.shiftId}).`;

  const submit = useMutation({
    mutationFn: (values: FormValues) =>
      api.createShiftSwap({
        requesterAssignmentId: values.requesterAssignmentId,
        // The server nulls these itself depending on swapType; sending null
        // rather than '' keeps the payload honest.
        targetEmployeeId: values.targetEmployeeId.trim() === '' ? null : values.targetEmployeeId.trim(),
        targetAssignmentId: values.targetAssignmentId.trim() === '' ? null : values.targetAssignmentId.trim(),
        swapType: values.swapType,
        // See the header: the open slot travels in the reason because the
        // table has no dedicated column for it.
        reason:
          values.swapType === 'PICKUP_OPEN' && pickupNote !== null
            ? `${values.reason.trim()} — ${pickupNote}`
            : values.reason,
        expiresAt: null,
      }),
    onSuccess: (response) => {
      setCreated(response);
      setPickedOpenShift(null);
      clearColleague();
      void queryClient.invalidateQueries({ queryKey: queryKeys.shiftSwaps });
      toast.success('Đã gửi yêu cầu đổi ca', 'Yêu cầu đang chờ duyệt.');
    },
    onError: (error) => toast.error('Không gửi được yêu cầu', errorMessage(error)),
  });

  const onSubmit = handleSubmit((values) => submit.mutate(values));

  return (
    <div className="pb-8">
      <ScreenHeader title="Đổi ca / nhận ca trống" />

      {created === null ? null : (
        <Banner tone="success" title="Đã gửi yêu cầu">
          Mã yêu cầu <strong>{created.swapRequestId}</strong> · trạng thái{' '}
          {approvalStatusLabel(created.status)}.
        </Banner>
      )}

      <form onSubmit={(event) => void onSubmit(event)} className="mt-3 space-y-4">
        <Card>
          <p className="label mb-2">Bạn muốn làm gì?</p>
          <div className="space-y-2">
            {(Object.keys(SWAP_TYPE_LABEL) as readonly SwapType[]).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setValue('swapType', type, { shouldValidate: true });
                  // A GIVEAWAY has no target assignment; drop a stale one.
                  if (type !== 'SWAP') setValue('targetAssignmentId', '');
                }}
                className={[
                  'min-h-touch-lg w-full rounded-2xl border px-3 text-left font-semibold active:bg-slate-50',
                  type === swapType ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 text-slate-700',
                ].join(' ')}
              >
                {SWAP_TYPE_LABEL[type]}
              </button>
            ))}
          </div>
        </Card>

        {/* --- ca trống: GET /staff/open-shifts --------------------------- */}
        {swapType === 'PICKUP_OPEN' ? (
          <Card>
            <p className="label mb-2">Ca trống có thể nhận</p>
            {openShifts.isLoading ? (
              <LoadingState label="Đang tìm ca trống…" />
            ) : openShifts.isError ? (
              <ErrorState message={errorMessage(openShifts.error)} onRetry={() => void openShifts.refetch()} />
            ) : (openShifts.data?.openShifts.length ?? 0) === 0 ? (
              <EmptyState
                title="Hiện không có ca trống phù hợp"
                description="Danh sách đã loại các ca trùng lịch của bạn, ca đã đủ người và ca yêu cầu kỹ năng bạn chưa có."
              />
            ) : (
              <ul className="space-y-2">
                {(openShifts.data?.openShifts ?? []).map((shift) => {
                  const picked = pickedOpenShift?.shiftId === shift.shiftId;
                  return (
                    <li key={shift.shiftId}>
                      <button
                        type="button"
                        onClick={() => setPickedOpenShift(picked ? null : shift)}
                        className={[
                          'min-h-touch-lg w-full rounded-2xl border px-3 py-2 text-left active:bg-slate-50',
                          picked ? 'border-brand-500 bg-brand-50' : 'border-slate-200',
                        ].join(' ')}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="block font-semibold text-slate-900">
                            {weekdayShort(shift.businessDate)} {shift.businessDate} · {formatTime(shift.startAt)} –{' '}
                            {formatTime(shift.endAt)}
                          </span>
                          <span className="shrink-0 text-sm font-bold text-brand-700">
                            {picked ? 'Đã chọn' : 'Nhận ca này'}
                          </span>
                        </span>
                        <span className="block text-sm text-slate-500">
                          {shift.name ?? shift.code ?? 'Ca làm việc'} · {shift.assignedCount}/
                          {shift.plannedHeadcount > 0 ? shift.plannedHeadcount : '—'} người
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {pickupNote === null ? null : (
              <p className="mt-2 rounded-xl bg-sky-50 p-3 text-sm text-sky-900">
                Sẽ ghi vào yêu cầu: <em>{pickupNote}</em>
              </p>
            )}
          </Card>
        ) : null}

        <Card>
          <p className="label mb-2">
            {swapType === 'PICKUP_OPEN' ? 'Ca của bạn dùng để đối chiếu' : 'Ca làm của bạn'}
          </p>
          {shifts.isLoading ? (
            <LoadingState label="Đang tải ca làm…" />
          ) : (shifts.data?.shifts.length ?? 0) === 0 ? (
            <EmptyState title="Bạn không có ca nào trong 30 ngày tới" />
          ) : (
            <ul className="space-y-2">
              {(shifts.data?.shifts ?? []).map((shift) => (
                <li key={shift.assignmentId}>
                  <button
                    type="button"
                    onClick={() => setValue('requesterAssignmentId', shift.assignmentId, { shouldValidate: true })}
                    className={[
                      'min-h-touch-lg w-full rounded-2xl border px-3 py-2 text-left active:bg-slate-50',
                      shift.assignmentId === requesterAssignmentId
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-slate-200',
                    ].join(' ')}
                  >
                    <span className="block font-semibold text-slate-900">
                      {shift.businessDate} · {formatTime(shift.startAt)} – {formatTime(shift.endAt)}
                    </span>
                    <span className="block text-sm text-slate-500">{shift.name ?? 'Ca làm việc'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {errors.requesterAssignmentId ? (
            <p className="field-error">{errors.requesterAssignmentId.message}</p>
          ) : null}
        </Card>

        {/* --- đồng nghiệp: GET /staff/colleagues ------------------------- */}
        {swapType === 'PICKUP_OPEN' ? null : (
          <Card>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="label">Đồng nghiệp nhận ca (không bắt buộc)</p>
              {pickedColleague === null ? null : (
                <button type="button" onClick={clearColleague} className="tap text-sm font-semibold text-brand-700">
                  Bỏ chọn
                </button>
              )}
            </div>
            <p className="mb-2 text-xs text-slate-500">
              Để trống nếu muốn quản lý tự sắp xếp. Danh sách chỉ hiện tên, vai trò và ca làm — không có thông
              tin cá nhân.
            </p>
            {colleagues.isLoading ? (
              <LoadingState label="Đang tải danh sách đồng nghiệp…" />
            ) : colleagues.isError ? (
              <ErrorState message={errorMessage(colleagues.error)} onRetry={() => void colleagues.refetch()} />
            ) : (colleagues.data?.colleagues.length ?? 0) === 0 ? (
              <EmptyState title="Không có đồng nghiệp nào có ca quanh thời gian này" />
            ) : (
              <ul className="max-h-72 space-y-2 overflow-y-auto">
                {(colleagues.data?.colleagues ?? []).map((colleague) => {
                  const picked = pickedColleague?.assignmentId === colleague.assignmentId;
                  return (
                    <li key={colleague.assignmentId}>
                      <button
                        type="button"
                        onClick={() => pickColleague(colleague)}
                        className={[
                          'min-h-touch-lg w-full rounded-2xl border px-3 py-2 text-left active:bg-slate-50',
                          picked ? 'border-brand-500 bg-brand-50' : 'border-slate-200',
                        ].join(' ')}
                      >
                        <span className="block font-semibold text-slate-900">
                          {colleague.name}
                          {colleague.roleCode === null ? '' : ` · ${colleague.roleCode}`}
                        </span>
                        <span className="block text-sm text-slate-500">
                          {colleague.businessDate} · {formatTime(colleague.startAt)} – {formatTime(colleague.endAt)}
                          {colleague.shiftName === null ? '' : ` · ${colleague.shiftName}`}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {swapType === 'SWAP' && pickedColleague !== null ? (
              <p className="mt-2 rounded-xl bg-sky-50 p-3 text-sm text-sky-900">
                Đổi với ca {pickedColleague.businessDate} của <strong>{pickedColleague.name}</strong>.
              </p>
            ) : null}
          </Card>
        )}

        <Card>
          <label className="label" htmlFor="swap-reason">
            Lý do
          </label>
          <textarea
            id="swap-reason"
            rows={3}
            className="field mt-1"
            placeholder="Ví dụ: con ốm, cần đổi ca tối sang ca sáng."
            {...register('reason')}
          />
          {errors.reason ? <p className="field-error">{errors.reason.message}</p> : null}
        </Card>

        <Button type="submit" size="lg" fullWidth loading={submit.isPending} disabled={!online}>
          Gửi yêu cầu
        </Button>
      </form>

      {/* --- yêu cầu đã gửi / nhận: GET /staff/shift-swaps ---------------- */}
      <SectionHeader title="Yêu cầu đổi ca" />
      {swapList.isLoading ? (
        <LoadingState />
      ) : swapList.isError ? (
        <ErrorState message={errorMessage(swapList.error)} onRetry={() => void swapList.refetch()} />
      ) : (swapList.data?.swaps.length ?? 0) === 0 ? (
        <EmptyState title="Chưa có yêu cầu đổi ca nào" />
      ) : (
        <ul className="space-y-2">
          {(swapList.data?.swaps ?? []).map((swap) => (
            <li key={swap.id}>
              <SwapCard swap={swap} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SwapCard({ swap }: { readonly swap: ShiftSwapItem }): JSX.Element {
  const typeLabel =
    swap.swapType in SWAP_TYPE_LABEL ? SWAP_TYPE_LABEL[swap.swapType as SwapType] : swap.swapType;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900">
            {swap.direction === 'SENT' ? 'Bạn gửi' : 'Gửi cho bạn'} · {typeLabel}
          </p>
          <p className="text-xs text-slate-400">Tạo lúc {formatTimeDate(swap.createdAt)}</p>
        </div>
        <StatusBadge tone={approvalStatusTone(swap.status)}>{approvalStatusLabel(swap.status)}</StatusBadge>
      </div>
      <p className="mt-2 text-sm text-slate-700">{swap.reason}</p>
      {swap.decisionNote === null ? null : (
        <p className="mt-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
          <strong>Phản hồi:</strong> {swap.decisionNote}
        </p>
      )}
      {swap.expiresAt === null ? null : (
        <p className="mt-1 text-xs text-slate-400">Hết hiệu lực {formatTimeDate(swap.expiresAt)}</p>
      )}
    </Card>
  );
}
